import React, { useEffect } from "react";

/* ---------------------------------------------------------------------------
   Shared admin building blocks — the single source of truth for the admin UI.
   Flat dark surfaces, hairline borders, single red accent (no gradients).
--------------------------------------------------------------------------- */

/* Page frame: heading + actions + optional description, above a content body */
export function PageHeader({ title, description, actions }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-white">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/* Row of small stat chips (label + value pairs) */
export function StatCardRow({ stats }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {stats.map((stat) => (
        <div key={stat.label} className="card px-4 py-3">
          <p className="eyebrow mb-1">{stat.label}</p>
          <p className="text-xl font-bold text-white tabular-nums">{stat.value}</p>
        </div>
      ))}
    </div>
  );
}

/* Standard bordered surface */
export function Panel({ children, className = "", padded = true }) {
  return (
    <div
      className={`card ${padded ? "p-4" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

/* Panel with a titled header row and optional right-side actions */
export function PanelHeader({ title, description, actions, className = "" }) {
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-line ${
        className || ""
      }`}
    >
      <div>
        <h2 className="text-sm font-semibold text-white">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/* Filter/search row used above tables */
export function Toolbar({ children }) {
  return (
    <div className="panel p-3">
      <div className="flex flex-col lg:flex-row lg:items-end gap-3">{children}</div>
    </div>
  );
}

export function ToolbarField({ label, children, className = "" }) {
  return (
    <div className={`flex-1 min-w-[180px] ${className}`}>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}

/* Standalone search input with magnifier icon — usable inside a Toolbar or alone */
export function SearchInput({ value, onChange, placeholder = "Search…", onClear }) {
  return (
    <div className="relative flex-1 min-w-[200px]">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="field !pl-9"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center text-gray-500 hover:text-white hover:bg-surface-600"
          aria-label="Clear search"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

/* Centered modal on a dim backdrop */
export function Modal({ title, onClose, children, footer, maxWidth = "max-w-md" }) {
  const titleId = React.useId();
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`w-full ${maxWidth} bg-surface-800 border border-line-strong rounded-xl shadow-2xl`}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <h3 id={titleId} className="text-base font-semibold text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-surface-700 transition-colors"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 px-4 py-3 border-t border-line">{footer}</div>}
      </div>
    </div>
  );
}

/* Danger confirmation dialog */
export function ConfirmDialog({ title = "Are you sure?", message, confirmLabel = "Delete", onConfirm, onCancel, busy = false }) {
  const handleCancel = () => {
    if (!busy) onCancel?.();
  };

  return (
    <Modal
      title={title}
      onClose={handleCancel}
      maxWidth="max-w-sm"
      footer={
        <>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-red-500/20 bg-red-500/10 text-red-300">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M6 7h12m-11 0 .8 12h8.4L17 7M9 7V4h6v3m-4 4v5m2-5v5" />
          </svg>
        </div>
        <p className="pt-1 text-sm leading-relaxed text-gray-300">{message}</p>
      </div>
    </Modal>
  );
}

/* States */
export function EmptyState({ title, message, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 rounded-xl bg-surface-700 border border-line-strong flex items-center justify-center text-gray-500 mb-4">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
      </div>
      <h3 className="text-base font-semibold text-white">{title}</h3>
      {message && <p className="mt-1 text-sm text-gray-500 max-w-sm">{message}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function PageSpinner({ label = "Loading…" }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-gray-400">
      <div className="w-8 h-8 border-2 border-line-strong border-t-accent rounded-full animate-spin" />
      <p className="mt-4 text-sm">{label}</p>
    </div>
  );
}

export function PageError({ message, onRetry }) {
  return (
    <div className="panel p-8 text-center">
      <p className="text-accent-soft font-medium">{message || "Something went wrong."}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="btn-secondary mt-4">
          Try again
        </button>
      )}
    </div>
  );
}
